<script setup lang="ts">
import { useId } from 'vue'

import type { CheckboxProps } from './types'

import { Icon } from '@/shared/ui/icon'
const model = defineModel<boolean>({ default: false })

withDefaults(defineProps<CheckboxProps>(), {
  indeterminate: false,
  disabled: false,
  error: false,
  tone: 'primary',
})

const emit = defineEmits<{ change: [checked: boolean] }>()

const inputId = useId()
const errorId = useId()
const checkboxToneClasses = {
  primary:
    'checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary',
  accent:
    'checked:border-accent checked:bg-accent indeterminate:border-accent indeterminate:bg-accent focus-visible:border-accent focus-visible:shadow-[0_0_0_2px_color-mix(in_srgb,var(--ui-color-accent)_50%,transparent)]',
} as const

function onChange(event: Event): void {
  emit('change', (event.target as HTMLInputElement).checked)
}
</script>

<template>
  <div
    class="ui-checkbox inline-flex flex-col gap-2xs font-sans"
    :class="[
      `ui-checkbox--${tone}`,
      { 'ui-checkbox--error': error, 'ui-checkbox--disabled': disabled },
      { 'opacity-60': disabled },
    ]"
  >
    <label
      class="ui-checkbox__control inline-flex cursor-pointer items-start gap-xs text-fg"
      :for="inputId"
    >
      <span
        class="ui-checkbox__box relative grid h-[1.125rem] w-[1.125rem] flex-none place-items-center"
      >
        <input
          :id="inputId"
          v-model="model"
          class="ui-checkbox__input peer m-0 h-full w-full cursor-pointer appearance-none rounded-sm border bg-surface outline-none transition-[background-color,border-color,box-shadow] duration-150 focus-visible:border-ring focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)] forced-colors:appearance-auto"
          :class="[
            checkboxToneClasses[tone],
            error
              ? 'border-danger focus-visible:shadow-[0_0_0_2px_var(--ui-color-danger)]'
              : 'border-border',
            { 'cursor-not-allowed': disabled },
          ]"
          type="checkbox"
          :name="name"
          :value="value"
          :indeterminate="indeterminate"
          :disabled="disabled"
          :aria-disabled="disabled"
          :aria-label="!label ? ariaLabel : undefined"
          :aria-invalid="error || undefined"
          :aria-describedby="error && errorMessage ? errorId : undefined"
          @change="onChange"
        />
        <Icon
          class="ui-checkbox__icon pointer-events-none absolute text-primary-fg opacity-0 transition-opacity duration-150 peer-checked:opacity-100 peer-indeterminate:opacity-0 forced-colors:hidden"
          name="check"
          :size="14"
        />
        <Icon
          class="ui-checkbox__icon ui-checkbox__icon--indeterminate pointer-events-none absolute text-primary-fg opacity-0 transition-opacity duration-150 peer-indeterminate:opacity-100 forced-colors:hidden"
          name="minus"
          :size="14"
        />
      </span>
      <span v-if="label" class="ui-checkbox__label text-[0.8125rem] leading-[1.125rem]">{{
        label
      }}</span>
    </label>
    <p
      v-if="error && errorMessage"
      :id="errorId"
      class="ui-checkbox__error m-0 ps-[1.625rem] text-[0.6875rem] font-medium leading-[1.2] text-danger"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>
